import type { HyperlinkContract } from '@paperbits/common/editing';
import type { MediaContract } from '@paperbits/common/media';
import { HyperlinkModel, HyperlinkTarget } from '@paperbits/common/permalinks';
import type { SiteKey } from '@tamtamy/paperbits-common/src/multisite/siteContract';
import { cmsContentPath } from '../cms/content/constants';
import type { ICmsContentPermalinkResolver } from '../cms/content/ICmsContentPermalinkResolver';
import {
    type CmsContentContract,
    cmsMetaExtractorAnyOption,
    cmsMetaExtractorAnyValue,
    type CmsPaperbitsPageRefContract,
    ContentTypeCmsMetaKeyOptionValueOption
} from '../cms/content/ko/CmsContentContract';
import type { ICustomPermalinkResolver } from '../media/ICustomPermalinkResolver';
import { getPageData } from './pageDataProvider';

function toSitePrefix(siteKey: SiteKey): string {
    const { prefixes } = getPageData() as any;
    return prefixes?.[siteKey] || siteKey.split('_')[1];
}

const normalizePermalink = (permalink: string) => (permalink?.startsWith('/') ? permalink : `/${permalink}`).replace(/\/$/, '');

export class CmsContentPermalinkResolver implements ICmsContentPermalinkResolver {
    constructor(
        private readonly mediaPermalinkResolver: ICustomPermalinkResolver,
    ) {}

    public canHandleTarget(targetKey: string): boolean {
        return targetKey.startsWith(cmsContentPath);
    }

    public async getUrlByTargetKey(_targetKey: string): Promise<string> {
        throw new Error('Unsupported method.');
    }

    private static getHyperlink(contract: CmsContentContract, hyperlinkContract?: HyperlinkContract): HyperlinkModel {
        const hyperlinkModel = new HyperlinkModel();
        hyperlinkModel.targetKey = `${cmsContentPath}${contract.id}`;
        hyperlinkModel.href = normalizePermalink(contract.permalink);
        hyperlinkModel.title = contract.title;

        if (hyperlinkContract) {
            hyperlinkModel.target = hyperlinkContract.target;
            hyperlinkModel.anchor = hyperlinkContract.anchor;
            hyperlinkModel.anchorName = hyperlinkContract.anchorName;
        }

        return hyperlinkModel;
    }

    public async getHyperlinkByTargetKey(_targetKey: string): Promise<HyperlinkModel> {
        throw new Error('Unsupported method.');
    }

    public async getHyperlinkByCmsContentContract(contract: CmsContentContract, siteKey: SiteKey): Promise<HyperlinkModel> {
        const hyperlink: HyperlinkModel = CmsContentPermalinkResolver.getHyperlink(contract);
        const type: ContentTypeCmsMetaKeyOptionValueOption = cmsMetaExtractorAnyOption<ContentTypeCmsMetaKeyOptionValueOption>(contract, 'content_type');
        let linkResolved: boolean = true;
        switch (type) {
            case ContentTypeCmsMetaKeyOptionValueOption.externalPage: {
                hyperlink.href = contract.url;
                if (contract.url && !contract.url.includes('javascript:void(0)')) {
                    hyperlink.target = '_blank'; // TODO Should also encapsulate rel="noreferrer"
                }
                break;
            }
            case ContentTypeCmsMetaKeyOptionValueOption.landingPage: {
                const ref: CmsPaperbitsPageRefContract = contract.paperbitsPageRef;
                if (ref?.permalink) {
                    const [group, lang] = siteKey.split('_');
                    hyperlink.href = `/${toSitePrefix(`${ref.siteGroup || group}_${lang}`)}${ref.permalink}`;
                } else {
                    linkResolved = false;
                }
                break;
            }
            case ContentTypeCmsMetaKeyOptionValueOption.pdf: {
                const value: string | MediaContract = cmsMetaExtractorAnyValue(contract, 'document');
                if (value) {
                    const mediaContract: MediaContract = typeof value === 'string' ? <MediaContract> JSON.parse(value) : value;
                    hyperlink.href = await this.mediaPermalinkResolver.getUrlFromMediaContract(mediaContract);
                    hyperlink.target = HyperlinkTarget.download;
                } else {
                    linkResolved = false;
                }
                break;
            }
            case ContentTypeCmsMetaKeyOptionValueOption.overlay: {
                break;
            }
        }
        if (!linkResolved) {
           return undefined;
        }
        return hyperlink;
    }
}